import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import localStorageUtil from '@/common/utils/localStorageUtil';
import translationDb from '@/common/utils/translationDb';
import translationStore from '@/common/utils/translationStore';
import { translateContentOnDemand } from '@/service/translationService';
import TelephoneConstant from '@/common/constant/TelephoneConstant';

const {
    ON_DEMAND_TRANSLATION_STATUS,
    ON_DEMAND_TRANSLATION_VIEW,
    ON_DEMAND_SOURCE_TYPE,
    ON_DEMAND_OUTLINE_KEY
} = TranslationConstant;

// Field types allowed for on-demand translation. Textarea shares the TEXT type
// (it only differs by UI), so TEXT + RICHTEXT cover text / textarea / richtext.
// Keep this list here so the translatable condition is easy to manage.
const TRANSLATABLE_FIELD_TYPES = [
    Constant.FORM_TYPE.FIELD_TEXT,
    Constant.FORM_TYPE.FIELD_RICHTEXT
];

// Ticket types rendered as email (subject-based title).
const EMAIL_TICKET_TYPES = [Constant.TICKET_TYPE.EMAIL, Constant.TICKET_TYPE.EMAIL_REPLY];

// --- Persistent browser cache (IndexedDB, usage = ONDEMAND) -----------------
// On-demand translations are cached per-record in translationDb so they survive
// F5 / a new tab (TCK-006 v1.6). Everything here is fail-safe: any failure
// degrades to the in-memory behaviour and never blanks/breaks the UI.

const ONDEMAND_USAGE = 'ONDEMAND';

/**
 * Resolve the cache scope: organization, target language (= user main language)
 * and whether translation is active. org / targetLanguage / enabled all come from
 * the shared translationStore config (set by useTranslationDictionaryLoader). org
 * is the dictionaryOrgId - the same key bootstrap cleanup keeps - so guest/partner
 * orgs (dictionaryOrgId = inviter) read/write the rows that survive F5.
 *
 * @returns {{ org: string, targetLanguage: string, enabled: boolean }}
 */
const getOnDemandScope = () => {
    const { enabled, mainLanguage, dictionaryOrgId } = translationStore.getConfig() || {};
    return { org: dictionaryOrgId, targetLanguage: mainLanguage, enabled: !!enabled };
};

/**
 * Build the per-record cache payload from a content record: only the translated
 * outline and each translated field value (originals are rebuilt from the ticket
 * on hydrate, so storing them would just waste space).
 *
 * @param {Object} record - a content record ({ translatedOutline, fields }).
 * @returns {{ translatedOutline: (string|null), fields: Object }}
 */
const buildOnDemandCachePayload = (record) => {
    const fields = {};
    Object.entries(record?.fields || {}).forEach(([key, field]) => {
        if (field?.translated != null) {
            fields[key] = field.translated;
        }
    });
    return { translatedOutline: record?.translatedOutline ?? null, fields };
};

/**
 * Build the record to cache from the current record + the translate result map,
 * mirroring exactly what setContentDetailOnDemandTranslations merges into the atom:
 * the reserved outline key becomes translatedOutline, and each remaining key is
 * applied only when it matches a real field of the record. Lets the caller persist
 * without reading the merged record back out of a Recoil updater (updaters must be
 * pure - no side-effects such as an IndexedDB write).
 *
 * @param {Object} record - the current content record ({ translatedOutline, fields }).
 * @param {Object} translationsByKey - { [fieldKey]: translatedText, [__outline__]: translatedOutline }.
 * @returns {{ translatedOutline: (string|null), fields: Object }} a record shape buildOnDemandCachePayload accepts.
 */
const buildMergedRecordForCache = (record, translationsByKey) => {
    const fields = { ...(record?.fields || {}) };
    let translatedOutline = record?.translatedOutline ?? null;
    Object.entries(translationsByKey || {}).forEach(([fieldKey, translated]) => {
        if (fieldKey === ON_DEMAND_OUTLINE_KEY) {
            translatedOutline = translated;
            return;
        }
        if (fields[fieldKey]) {
            fields[fieldKey] = { ...fields[fieldKey], translated };
        }
    });
    return { ...record, fields, translatedOutline };
};

/**
 * Persist a translated content record to IndexedDB (fire-and-forget). Keyed by
 * sourceId (ticketId / child email id). No-op when translation is off, the DB is
 * unavailable, there is nothing to store, or storage is near its quota.
 *
 * @param {Object} params
 * @param {string} params.sourceId - record key (ticketId / child email id).
 * @param {string} params.sourceType - ON_DEMAND_SOURCE_TYPE value.
 * @param {Object} params.record - the translated content record.
 */
const persistOnDemandTranslation = async ({ sourceId, sourceType, record }) => {
    try {
        const { org, targetLanguage, enabled } = getOnDemandScope();
        if (!enabled || !org || !targetLanguage || !sourceId) {
            return;
        }
        if (!translationDb.isAvailable()) {
            return;
        }

        const payload = buildOnDemandCachePayload(record);
        // Nothing worth caching (no translated outline and no translated fields).
        if (payload.translatedOutline == null && Object.keys(payload.fields).length === 0) {
            return;
        }

        // Guard the quota before writing a potentially large row (rich text ~350k).
        const hasRoom = await translationDb.hasStorageHeadroom();
        if (!hasRoom) {
            return;
        }

        const translatedText = JSON.stringify(payload);
        await translationDb.putContentEntry({
            affiliationOrganization: org,
            targetLanguage,
            sourceId,
            sourceType,
            translatedText
        });

        await translationDb.enforceScopeLimit(
            org,
            ONDEMAND_USAGE,
            targetLanguage,
            Constant.TRANSLATION_CACHE_LIMIT[ONDEMAND_USAGE]
        );
    } catch (e) {
        // fail-safe: a cache write failure must not affect the UI (the translation
        // is already shown from the atom).
    }
};

/**
 * Hydrate a content record from the IndexedDB cache (async, fire-and-forget).
 * Called at the end of initContentDetailOnDemand right after an idle record is
 * built. If a cached translation exists for this ticket, the record is flipped to
 * TRANSLATED with the toggle ON so the user sees the translation immediately after
 * F5 / a new tab. The cache is kept regardless of ticket edits or status changes;
 * it is only dropped on project switch / logout.
 *
 * Race-safe: the updater only overwrites a record that is still IDLE, so a user
 * who toggled translation before IndexedDB returned is never clobbered.
 *
 * @param {Object} params
 * @param {Function} params.setContentDetailOnDemand - Recoil setter.
 * @param {Object} params.ticket - the ticket (needs id + updatedAt).
 */
export const hydrateOnDemandFromCache = async ({ setContentDetailOnDemand, ticket }) => {
    try {
        if (typeof setContentDetailOnDemand !== 'function' || !ticket?.id) return;

        const { org, targetLanguage, enabled } = getOnDemandScope();
        if (!enabled || !org || !targetLanguage) {
            return;
        }
        if (!translationDb.isAvailable()) {
            return;
        }

        // The cache is not invalidated by edits/status changes - any stored
        // translation for this ticket is returned and shown.
        const cached = await translationDb.readContentEntry(
            org,
            targetLanguage,
            ticket.id
        );
        if (!cached) {
            return; // no cached translation for this ticket -> keep idle
        }

        let payload;
        try {
            payload = JSON.parse(cached);
        } catch (parseErr) {
            return; // corrupted cache value -> treat as miss
        }

        setContentDetailOnDemand((prev) => {
            const record = prev[ticket.id];
            // Only hydrate an untouched (idle) record: respect any manual toggle
            // that happened while IndexedDB was resolving.
            if (!record || record.status !== ON_DEMAND_TRANSLATION_STATUS.IDLE) {
                return prev;
            }

            const nextFields = { ...record.fields };
            Object.entries(payload?.fields || {}).forEach(([key, translated]) => {
                if (nextFields[key]) {
                    nextFields[key] = { ...nextFields[key], translated };
                }
            });

            return {
                ...prev,
                [ticket.id]: {
                    ...record,
                    fields: nextFields,
                    translatedOutline: payload?.translatedOutline ?? record.translatedOutline,
                    status: ON_DEMAND_TRANSLATION_STATUS.TRANSLATED,
                    activeView: ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
                }
            };
        });
    } catch (e) {
        // fail-safe: hydrate failure -> record stays idle, user toggles to translate.
    }
};

/**
 * Clear the persistent on-demand translation cache for every ticket/knowledge
 * (the whole ONDEMAND slot, all orgs/languages). Called when the session scope
 * changes - switching project or logging out - so the next open re-translates
 * instead of showing a cache from the previous scope. Fire-and-forget and
 * fail-safe: a failure never blocks the project switch / logout.
 */
export const clearOnDemandCache = async () => {
    try {
        if (!translationDb.isAvailable()) {
            return;
        }
        await translationDb.clearAllOnDemand();
    } catch (e) {
        // fail-safe: a clear failure must never block project switch / logout.
    }
};

/**
 * Resolve the placeholder shown when a ticket has no outline/title, based on the
 * ticket "kind": email uses the subject placeholder, a business-app ticket (a
 * field list containing the business-app-name field) uses the app placeholder,
 * everything else uses the generic outline placeholder.
 *
 * @param {Object} ticket - The ticket data (uses ticketType).
 * @param {Array} fieldList - The ticket's rendered fields.
 * @returns {string} the matching *_NO_TITLE constant.
 */
const resolveNoTitlePlaceholder = (ticket, fieldList) => {
    if (EMAIL_TICKET_TYPES.includes(ticket?.ticketType)) {
        return Constant.EMAIL_TICKET_NO_TITLE;
    }

    const hasBusinessAppField = Array.isArray(fieldList)
        && fieldList.some((field) => field?.sysField?.toLowerCase() === Constant.SYS_FIELD.BUSINESS_APP_NAME);
    if (hasBusinessAppField) {
        return Constant.APP_TICKET_NO_TITLE;
    }

    return Constant.TICKET_NO_TITLE;
};

/**
 * Build the on-demand translation fields map from the ticket's rendered field
 * list (fieldListDefaultData).
 *
 * Each entry is keyed by the field's fieldId and stores the field's current
 * value as its original, with an empty translation (not requested yet).
 *
 * @param {Array} fieldList - The ticket's rendered fields (fieldListDefaultData).
 * @returns {Object} fields map: { [fieldId]: { original, translated } }
 */
export const buildContentDetailOnDemandFields = (fieldList, ticket) => {
    if (!Array.isArray(fieldList)) {
        return {};
    }

    return fieldList.reduce((acc, fieldData) => {
        // Ticket/knowledge fields use `fieldId`; email fields use `id`.
        const fieldKey = fieldData?.fieldId ?? fieldData?.id;
        if (!fieldKey) {
            return acc;
        }

        // Ticket/knowledge pass fieldListDefaultData (value in `defaultValue`);
        // email passes the raw listField (value in `value`). Support both.
        const rawValue = fieldData.defaultValue ?? fieldData.value;
        let original = fieldData?.type === Constant.FORM_TYPE.FIELD_TEXT
            ? (typeof rawValue === 'string' ? rawValue : (rawValue ?? ''))
            : Utils.convertJSONDataToObject(rawValue);

        // special case
        if ([
            Constant.SYSTEM_FIELD.ESCALATION_ORIGIN_CUSTOMER,
            Constant.SYSTEM_FIELD.ESCALATION_TARGET,
            TelephoneConstant.TELEPHONE.SYSTEM_FIELD.RELATED_TICKETS,
            TelephoneConstant.TELEPHONE.SYSTEM_FIELD.NEXT_CALL
        ].includes(fieldData.sysField) && !Utils.isEmptyObject(ticket)) {
            switch (fieldData.sysField) {
                case Constant.SYSTEM_FIELD.ESCALATION_ORIGIN_CUSTOMER: {
                    const ticketParent = Utils.convertJSONDataToObject(ticket?.parentTicket) || {};
                    original = ticketParent?.outline || '';
                    break;
                }
                case Constant.SYSTEM_FIELD.ESCALATION_TARGET:
                    original = ticket?.outline || '';
                    break;
                case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.RELATED_TICKETS:
                case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.NEXT_CALL: {
                    const linkedTicket = fieldData?.value
                        ? Utils.convertJSONDataToObject(fieldData?.value) : {};
                    original = linkedTicket.outline || '';
                    break;
                }
                default:
                    break;
            }
        }
        
        acc[fieldKey] = {
            original,
            translated: null,
            sysField: fieldData?.sysField,
            type: fieldData?.type
        };
        return acc;
    }, {});
};

/**
 * Build the on-demand translation fields map for a PDF ticket (file/page
 * knowledge). PDF fields keep their value in `dataValue`:
 * @param {Array} fieldList - the PDF ticket's rendered fields (fieldListDefaultData).
 * @returns {Object} fields map: { [fieldId]: { original, translated, sysField, type } }
 */
export const buildPdfContentDetailOnDemandFields = (fieldList) => {
    if (!Array.isArray(fieldList)) {
        return {};
    }

    let buildField = fieldList.filter(f => !TranslationConstant.IGNORE_ON_DEMAND_FIELDS.includes(f?.sysField));

    buildField = buildField.reduce((acc, fieldData) => {
        const fieldKey = fieldData?.id;

        if (!fieldKey) return acc;

        const sysField = fieldData?.sysField;

        // define value
        let fieldOriginal = typeof fieldData?.dataValue === 'string' ? fieldData.dataValue : '';
        if ([Constant.SYSTEM_FIELD.PARENT, Constant.SYSTEM_FIELD.CHILDREN].includes(sysField)) {
            fieldOriginal = Utils.convertJSONDataToObject(fieldData?.dataValue)?.outline || '';
        }
        if (!fieldOriginal) return acc;
        
        // define field type
        let fieldType = Constant.FORM_TYPE.FIELD_TEXT;
        if (sysField === Constant.SYSTEM_FIELD.BOOKMARK) {
            fieldType = Constant.FORM_TYPE.FIELD_RICHTEXT;
        }

        acc[fieldKey] = {
            original: fieldOriginal,
            translated: null,
            sysField: sysField,
            type: fieldType
        };
        return acc;
    }, {});

    return buildField;
};

/**
 * Build the full on-demand translation record for a single ticket.
 *
 * @param {Object} params
 * @param {Object} params.ticket - The ticket data.
 * @param {Array} params.fieldList - The ticket's rendered fields (fieldListDefaultData).
 * @param {string} [params.ticketOutline] - The outline/title as rendered by the caller.
 * @param {string} [params.rootTabId] - The tab id that shows this record when it is keyed
 *   by a DIFFERENT id than that tab. By the system rule tabId === ticketId, so this is the
 *   tab's ticket id (email: record keyed by child id, tab keyed by parent/root id). Adds a
 *   `tab` link marking this record active. Empty for records keyed by their own tab id.
 * @returns {Object} record: { ticketType, ticketOutline, translatedOutline, tab?, status, activeView, fields }
 */
export const buildContentDetailOnDemandRecord = ({ ticket, fieldList, ticketOutline = '', rootTabId = '' }) => {
    
    // PDF ticket types (file / page knowledge). Their field list has a different
    // shape (value in `dataValue`, richtext bookmark stored as a JSON outline array),
    // so they build their translation fields with a dedicated builder below.
    const PDF_TICKET_TYPES = [
        `${Constant.TICKET_TYPE.PDF_FILE_KNOWLEDGE}`,
        `${Constant.TICKET_TYPE.PDF_PAGE_KNOWLEDGE}`
    ];

    let buildFields = {};
    if (PDF_TICKET_TYPES.includes(ticket?.ticketType)) {
        buildFields = buildPdfContentDetailOnDemandFields(fieldList);
    } else {
        buildFields = buildContentDetailOnDemandFields(fieldList, ticket);
    }

    let contentDetailOnDemand = {
        ticketType: ticket?.ticketType,
        ticketOutline: Utils.isEmptyString(ticketOutline)
            ? resolveNoTitlePlaceholder(ticket, fieldList)
            : ticketOutline,
        translatedOutline: null,
        status: ON_DEMAND_TRANSLATION_STATUS.IDLE,
        activeView: ON_DEMAND_TRANSLATION_VIEW.ORIGINAL,
        ticketMode: Constant.DISPLAY_MODE.VIEW,
        fields: buildFields
    };

    const hasRootTabId = !Utils.isEmptyString(rootTabId);
    if (hasRootTabId) {
        // Present only when the record is keyed by a different id than the tab that shows
        // it (email: record keyed by child id, tab keyed by root/parent id). `tabId` is that
        // tab's id and `isActive` is whether this record is the one the tab is currently
        // showing. Absent for tickets keyed by their own tab id (chat/QA/knowledge/...).
        return {
            ...contentDetailOnDemand,
            tab: { tabId: rootTabId, isActive: true }
        }
    }

    return contentDetailOnDemand;
};

/**
 * Whether a content record has already been translated (or is being translated).
 *
 * A record counts as "translated" once its status is TRANSLATED or LOADING. In
 * those states the original values must NOT be overwritten, so the translated
 * result is preserved and re-rendered (per spec: đã dịch rồi thì không dịch lại).
 *
 * @param {Object} record - The content record (may be undefined).
 * @returns {boolean} true when the record is translated/loading.
 */
const isContentDetailOnDemandTranslated = (record) => {
    if (!record) {
        return false;
    }
    return [
        ON_DEMAND_TRANSLATION_STATUS.TRANSLATED,
        ON_DEMAND_TRANSLATION_STATUS.LOADING
    ].includes(record.status);
};

/**
 * Initialize the on-demand translation state for a ticket, keyed by ticketId.
 *
 * Call this right after the ticket detail (and its field list) has been built.
 * It merges the new record into the existing atom state without touching other
 * tickets.
 *
 * @param {Function} setContentDetailOnDemand - Recoil setter for contentDetailOnDemandAtom.
 * @param {Object} ticket - The fetched ticket detail object (must have an id).
 * @param {Array} fieldList - The ticket's rendered fields (fieldListDefaultData).
 * @param {string} ticketOutline - The outline/title as rendered by the caller
 *   (see buildContentDetailOnDemandRecord).
 * @param {string} [rootTabId] - The tab's ticket id when it differs from the record key
 *   (email: record keyed by child id, tab keyed by root/parent id). Passing it marks this
 *   record as the tab's active one and clears the flag on the tab's other records, so the
 *   tab title follows the record currently shown.
 */
export const initContentDetailOnDemand = (setContentDetailOnDemand, ticket, fieldList, ticketOutline = '', rootTabId = '') => {
    if (typeof setContentDetailOnDemand !== 'function' || !ticket?.id) {
        return;
    }

    const hasRootTabId = !Utils.isEmptyString(rootTabId);

    setContentDetailOnDemand((prev) => {
        // For a record linked to a root tab, the active record must always follow the page
        // being viewed - even when it is already translated (skip-rebuild). So mark the
        // active record on every call, before deciding whether to rebuild fields.
        let base = prev;
        if (hasRootTabId) {
            base = markActiveTabRecord(prev, rootTabId, ticket.id);
        }

        // Already translated (or loading): keep its translated values (only the
        // active flag above may have changed). The translation is NOT invalidated
        // when the ticket is edited or its status changes - the cache lives until
        // the project is switched or the user logs out.
        const existing = base[ticket.id];
        if (existing && isContentDetailOnDemandTranslated(existing)) {
            return base;
        }

        return {
            ...base,
            [ticket.id]: buildContentDetailOnDemandRecord({ticket, fieldList, ticketOutline, rootTabId})
        };
    });

    // Persistent cache hydrate (fire-and-forget): if a cached translation exists
    // for this ticket, flip the just-built idle record to translated so F5 / a new
    // tab shows the translation immediately.
    hydrateOnDemandFromCache({ setContentDetailOnDemand, ticket });
};

/**
 * Mark the record currently shown by a tab as active (tab.isActive) and clear it on
 * the tab's other records, so exactly one record per tab is active at a time.
 * Returns a new state object (only the changed records are replaced); the given state
 * is returned unchanged when nothing needs updating.
 *
 * @param {Object} prev - The current contentDetailOnDemand state.
 * @param {string} rootTabId - The tab id (by the system rule, the root/parent ticket id).
 * @param {string} activeRecordId - The record id currently shown by the tab.
 * @returns {Object} the next state.
 */
const markActiveTabRecord = (prev, rootTabId, activeRecordId) => {
    let next = prev;
    Object.entries(prev).forEach(([id, record]) => {
        if (record?.tab?.tabId !== rootTabId) return;

        const shouldBeActive = id === activeRecordId;
        if (record.tab.isActive === shouldBeActive) return;

        // Copy-on-write: only clone once a change is actually needed.
        if (next === prev) next = { ...prev };
        next[id] = { ...record, tab: { ...record.tab, isActive: shouldBeActive } };
    });
    return next;
};

/**
 * Resolve the on-demand record an EMAIL TAB should use for its title, looked up by
 * the parent ticket id (the email tab's tab.id is the parent ticket id, while the
 * atom is keyed by each CHILD email id).
 *
 * An email tab shows one title but paginates across several child email tickets,
 * each with its own record. Returns the ACTIVE record (the one currently shown,
 * flagged `tab.isActive`) so switching page switches the title; whether that title
 * is the translation or the original is decided downstream by getDisplayOutline.
 * Returns null when no active record exists, so the caller keeps the tab title.
 *
 * @param {Object} contentDetailOnDemand - The contentDetailOnDemandAtom value.
 * @param {string} tabId - The email tab id (the parent ticket id).
 * @returns {Object|null} the active record, or null.
 */
export const getEmailOnDemandRecordOnTabChatPanel = (contentDetailOnDemand, tabId) => {
    if (!contentDetailOnDemand || Utils.isEmptyString(tabId)) return null;

    // Just find the tab's active record; whether to show its translation vs original is
    // decided downstream by getDisplayOutline (mode/view/translatedOutline checks).
    const record = Object.values(contentDetailOnDemand).find(
        (childRecord) => childRecord?.tab?.tabId === tabId && childRecord?.tab?.isActive
    );

    return record || null;
};

/**
 * Update the translation status of a single content record, keyed by contentId.
 * No-op if the record does not exist yet.
 *
 * @param {Function} setContentDetailOnDemand - Recoil setter for contentDetailOnDemandAtom.
 * @param {string} contentId - The content record key (ticketId / child email id).
 * @param {string} status - One of ON_DEMAND_TRANSLATION_STATUS values.
 */
export const setContentDetailOnDemandStatus = (setContentDetailOnDemand, contentId, status) => {
    if (typeof setContentDetailOnDemand !== 'function' || !contentId) {
        return;
    }

    setContentDetailOnDemand((prev) => {
        const record = prev[contentId];
        if (!record) {
            return prev;
        }
        return {
            ...prev,
            [contentId]: {
                ...record,
                status
            }
        };
    });
};

/**
 * Update the active view (bản gốc / bản dịch) of a single content record, keyed
 * by contentId. Kept in the atom so the detail component (which renders the
 * fields) can read the view and show the translated value. No-op if the record
 * is absent.
 *
 * @param {Function} setContentDetailOnDemand - Recoil setter for contentDetailOnDemandAtom.
 * @param {string} contentId - The content record key.
 * @param {string} activeView - One of ON_DEMAND_TRANSLATION_VIEW values.
 */
export const setContentDetailOnDemandActiveView = (setContentDetailOnDemand, contentId, activeView) => {
    if (typeof setContentDetailOnDemand !== 'function' || !contentId) {
        return;
    }

    setContentDetailOnDemand((prev) => {
        const record = prev[contentId];
        if (!record) {
            return prev;
        }
        return {
            ...prev,
            [contentId]: {
                ...record,
                activeView
            }
        };
    });
};

/**
 * Update the detail-screen mode (VIEW / EDIT) of a single content record, keyed
 * by contentId. Kept on the record so every reader (tab title, header, fields)
 * shares one source of truth: while EDIT, they render the original regardless of
 * activeView, and activeView is preserved so returning to VIEW restores the
 * translation the user was viewing. No-op if the record is absent.
 *
 * @param {Function} setContentDetailOnDemand - Recoil setter for contentDetailOnDemandAtom.
 * @param {string} contentId - The content record key.
 * @param {string} ticketMode - One of Constant.DISPLAY_MODE values (VIEW/EDIT).
 */
export const setContentDetailOnDemandTicketMode = (setContentDetailOnDemand, contentId, ticketMode) => {
    if (typeof setContentDetailOnDemand !== 'function' || !contentId) {
        return;
    }

    setContentDetailOnDemand((prev) => {
        const record = prev[contentId];
        if (!record || record.ticketMode === ticketMode) {
            return prev;
        }
        return {
            ...prev,
            [contentId]: {
                ...record,
                ticketMode
            }
        };
    });
};

/**
 * Merge translated values into a content record's fields and set its status.
 * No-op if the record does not exist.
 *
 * @param {Function} setContentDetailOnDemand - Recoil setter for contentDetailOnDemandAtom.
 * @param {string} contentId - The content record key.
 * @param {Object} translationsByKey - { [fieldKey]: translatedText }.
 * @param {string} status - One of ON_DEMAND_TRANSLATION_STATUS values.
 */
export const setContentDetailOnDemandTranslations = (
    setContentDetailOnDemand,
    contentId,
    translationsByKey,
    status
) => {
    if (typeof setContentDetailOnDemand !== 'function' || !contentId) return;

    setContentDetailOnDemand((prev) => {
        const record = prev[contentId];
        if (!record) {
            return prev;
        }

        // An empty map is valid (nothing needed translating): we still update
        // status/activeView below. Only merge when there are translations to apply.
        // The outline lives under a reserved key (not a real field), so pull it out
        // into `translatedOutline` and merge the rest into the fields map.
        const nextFields = { ...record.fields };
        let nextTranslatedOutline = record.translatedOutline;
        Object.entries(translationsByKey || {}).forEach(([fieldKey, translated]) => {
            if (fieldKey === ON_DEMAND_OUTLINE_KEY) {
                nextTranslatedOutline = translated;
                return;
            }
            if (nextFields[fieldKey]) {
                nextFields[fieldKey] = { ...nextFields[fieldKey], translated };
            }
        });

        // When the translation succeeds, switch the toggle ON immediately (show
        // bản dịch) per spec AC-2. On error keep bản gốc (toggle OFF).
        const nextActiveView = status === ON_DEMAND_TRANSLATION_STATUS.TRANSLATED
            ? ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
            : ON_DEMAND_TRANSLATION_VIEW.ORIGINAL;

        return {
            ...prev,
            [contentId]: {
                ...record,
                activeView: nextActiveView,
                fields: nextFields,
                translatedOutline: nextTranslatedOutline,
                status
            }
        };
    });
};

/**
 * Pick the fields eligible for on-demand translation from a record's fields map,
 * by field type only (Text/TextArea/RichText). Empty values are NOT filtered here:
 * the BE fetches the real content and skips empty fields, so sending an empty
 * field is harmless. Used both to decide whether to show the toggle and to build
 * the request payload.
 *
 * @param {Object} fields - record.fields ({ [fieldId]: { original, type, ... } }).
 * @returns {Array<{ fieldId: string, original: any, type: string }>}
 */
export const getTranslatableFields = (fields) => {
    if (!fields || typeof fields !== 'object') {
        return [];
    }

    const translatableFields = Object.entries(fields).reduce((acc, [fieldId, field]) => {
        // Only a plain string is real text content. An empty value becomes {} via
        // convertJSONDataToObject; stringifying that yields '[object Object]', so guard by type.
        const original = typeof field?.original === 'string' ? field.original.trim() : '';
        if (TRANSLATABLE_FIELD_TYPES.includes(field?.type)) {
            acc.push({ fieldId, original, type: field.type, sysField: field.sysField });
        }
        return acc;
    }, []);

    return translatableFields;
};

/**
 * Translate all translatable fields of a content record on demand, then merge
 * the results into the atom. Reads org/user from localStorage; the target language
 * is resolved by the BE from the user's main language. Fail-safe: individual field
 * failures keep the original; a total failure sets the record status to ERROR.
 * A stale-data failure (INVALID_PARAM: the ticket/fields no longer resolve on the BE)
 * opens the "please reload" modal via setIsOpenModalReloadPage.
 *
 * @param {Object} params
 * @param {Function} params.setContentDetailOnDemand - Recoil setter for contentDetailOnDemandAtom.
 * @param {string} params.ticketId - The content record key (ticketId).
 * @param {Object} params.record - The current content record ({ status, fields, ... }).
 * @param {Function} [params.setIsOpenModalReloadPage] - Opens the DataChangePleaseReloadModal
 *   when the BE reports the viewed content is stale (INVALID_PARAM).
 */
export const requestContentDetailOnDemandTranslation = async ({
    setContentDetailOnDemand,
    ticketId,
    record,
    setIsOpenModalReloadPage
}) => {
    const translatableFields = getTranslatableFields(record?.fields);
    // The outline/title is translated too (sent as text since the BE can't fetch it).
    const outline = record?.ticketOutline;

    if (Utils.isEmptyArray(translatableFields) && Utils.isEmptyString(outline)) {
        // Nothing to translate: mark as TRANSLATED and switch the toggle ON so the
        // UI behaves consistently (there are simply no translated values to show).
        setContentDetailOnDemandStatus(
            setContentDetailOnDemand,
            ticketId,
            ON_DEMAND_TRANSLATION_STATUS.TRANSLATED
        );
        setContentDetailOnDemandActiveView(
            setContentDetailOnDemand,
            ticketId,
            ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
        );
        return;
    }

    setContentDetailOnDemandStatus(
        setContentDetailOnDemand,
        ticketId,
        ON_DEMAND_TRANSLATION_STATUS.LOADING
    );

    const affiliationOrganization = localStorageUtil.getAffiliationOrganization();
    const userId = localStorageUtil.getUserDetailId();

    // Send the field ids + their type (the BE fetches each field's content itself via GSI3 and picks
    // the mimeType from the type) plus the outline text (BE-uncomputable, so sent directly). The BE
    // returns a { [fieldId]: translatedText, [__outline__]: translatedOutline } map (offloaded to S3).
    const listField = translatableFields.map(translatableField => {
        const { fieldId, type, original, sysField } = translatableField;
        if (type === Constant.FORM_TYPE.FIELD_TEXT) {
            return ({ fieldId, type, original, sysField });
        }
        return ({ fieldId, type, sysField });
    });
    const translateArgs = {
        sourceType: ON_DEMAND_SOURCE_TYPE.TICKET,
        ticketId, listField, outline
    }
    const { translations: translationsByKey, errorCode } = await translateContentOnDemand(
        affiliationOrganization,
        userId,
        translateArgs
    );

    // Handle invalid translate by errorCode
    if (errorCode) return setIsOpenModalReloadPage(true);

    // Distinguish a real failure from a successful-but-empty result:
    //   - null       -> the service failed (network / S3 / fetch) -> ERROR, keep originals.
    //   - {} or {..} -> translation succeeded (an empty map just means nothing needed
    //                   translating, e.g. all fields empty) -> TRANSLATED.
    const nextStatus = translationsByKey == null
        ? ON_DEMAND_TRANSLATION_STATUS.ERROR
        : ON_DEMAND_TRANSLATION_STATUS.TRANSLATED;

    setContentDetailOnDemandTranslations(
        setContentDetailOnDemand,
        ticketId,
        translationsByKey || {},
        nextStatus
    );

    // Persist to the browser cache on success so the translation survives F5 /
    // a new tab. Fire-and-forget: build the merged record from the current record
    // + the translate result (the same merge setContentDetailOnDemandTranslations
    // applies to the atom) and write it out - no side-effect inside a state updater.
    if (nextStatus === ON_DEMAND_TRANSLATION_STATUS.TRANSLATED) {
        const mergedRecord = buildMergedRecordForCache(record, translationsByKey);
        persistOnDemandTranslation({
            sourceId: ticketId,
            sourceType: ON_DEMAND_SOURCE_TYPE.TICKET,
            record: mergedRecord
        });
    }
};

/**
 * Resolve the value a field should display given the record's active view.
 *
 * When the toggle is ON (activeView = TRANSLATION), a field that has a translated
 * value shows it; otherwise (toggle OFF, no translation, or field not
 * translatable) it falls back to the original value passed in by the caller.
 *
 * @param {Object} record - The content record ({ activeView, fields }).
 * @param {string} fieldKey - The field key (fieldId / id).
 * @param {*} originalValue - The value the field would normally render.
 * @returns {*} translated value when toggle ON and available, else originalValue.
 */
export const getFieldTranslatedValue = (record, fieldKey, originalValue) => {
    // In edit mode the field is being edited, so always show the original.
    if (
        !record ||
        record.ticketMode === Constant.DISPLAY_MODE.EDIT ||
        record.activeView !== ON_DEMAND_TRANSLATION_VIEW.TRANSLATION
    ) {
        return originalValue;
    }

    const translated = record.fields?.[fieldKey]?.translated;
    return translated != null ? translated : originalValue;
};

/**
 * Resolve the outline/title to display for a content record given its active view.
 *
 * When the toggle is ON (activeView = TRANSLATION) and a translated outline is
 * available, the translation is shown; otherwise the original outline is used.
 *
 * @param {Object} record - The content record ({ activeView, ticketOutline, translatedOutline }).
 * @returns {string} the outline to display.
 */
export const getDisplayOutline = (record) => {
    if (!record) {
        return '';
    }

    // Org multi-language off -> always the original outline, never the cached translation.
    if (
        getOnDemandScope().enabled &&
        record.ticketMode !== Constant.DISPLAY_MODE.EDIT &&
        record.activeView === ON_DEMAND_TRANSLATION_VIEW.TRANSLATION &&
        !Utils.isEmptyString(record.translatedOutline)
    ) {
        return record.translatedOutline;
    }

    return record.ticketOutline || '';
};
